import { useCallback, useEffect, useMemo, useState } from "react";
import { useMediaDevices } from "react-media-devices";

export type ReadCompleted = (inventoryId?: string | null, warehouseLocation?: string | null) => void;

export type UserScannerProps = {
  readCompleted: ReadCompleted;
};

const getCameraDefaultFromLocalStorage = (): MediaDeviceInfo | null => {
  const storageValue = localStorage.getItem("cameraDefault");
  if (!storageValue) return null;
  return JSON.parse(storageValue);
};

export default function useQrScanner({ readCompleted }: { readCompleted: ReadCompleted }) {
  const [processing, setProcessing] = useState(false);
  const [cameraFacing, setCameraFacing] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<MediaDeviceInfo>();

  const { devices: mediaDevices } = useMediaDevices({
    constraints: { video: true, audio: false },
  });

  const devices = useMemo(
    () => mediaDevices?.filter(({ kind, deviceId }) => kind === "videoinput" && deviceId) ?? [],
    [mediaDevices],
  );

  const onScan = useCallback(
    (data: { text: string } | null) => {
      if (data && !processing) {
        try {
          readCompleted(data.text);
        } catch {
          readCompleted(null, data.text);
        } finally {
          setProcessing(true);
        }
      }
    },
    [readCompleted, processing, setProcessing],
  );

  const onError = useCallback(() => {}, []);

  useEffect(() => {
    if (!devices.length) return;

    const storedDevice = getCameraDefaultFromLocalStorage();
    const validStoredDevice = storedDevice
      ? devices.find(({ deviceId }) => deviceId === storedDevice.deviceId)
      : undefined;

    setSelectedDevice(validStoredDevice ?? devices[0]);
  }, [devices]);

  useEffect(() => {
    const localStorageValue = getCameraDefaultFromLocalStorage();
    if (selectedDevice && (localStorageValue === null || localStorageValue.deviceId !== selectedDevice?.deviceId)) {
      localStorage.setItem("cameraDefault", JSON.stringify(selectedDevice));
    }
  }, [selectedDevice]);

  return {
    processing,
    devices,
    selectedDevice,
    setCameraFacing,
    cameraFacing,
    setSelectedDevice,
    readerProps: {
      delay: 300,
      style: { width: "100%" },
      constraints: {
        video: {
          deviceId: {
            exact: selectedDevice?.deviceId,
          },
          facingMode: cameraFacing ? "user" : "environment",
        },
      },
      onScan,
      onError,
    },
  };
}
